package com.tovtalk.data.local

import com.russhwolf.settings.Settings
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/**
 * Token manager with persistence.
 * Uses in-memory cache so the value is visible immediately after set.
 */
object TokenManager {
    private var settings: Settings? = null
    private const val KEY_TOKEN = "auth_token"
    private const val KEY_REFRESH_TOKEN = "refresh_token"
    private const val KEY_DEVICE_TOKEN = "device_token"
    private const val KEY_IS_GUEST = "is_guest"
    private const val KEY_USER_ID = "user_id"

    // Legacy keys (kept for migration/cleanup on init)
    private const val KEY_GUEST_TOKEN = "guest_token"
    private const val KEY_GUEST_ID = "guest_id"
    private const val KEY_GUEST_KASHRUT_LEVEL = "guest_kashrut_level"
    private const val KEY_GUEST_CATEGORY_IDS = "guest_category_ids"

    private var memoryAccessToken: String? = null
    private var memoryRefreshToken: String? = null
    private var memoryDeviceToken: String? = null
    private var memoryIsGuest: Boolean = false
    private var memoryUserId: Int? = null

    /**
     * Observable auth state. UI uses [authStateFlow].collectAsState() so composables
     * recompose when guest→registered transitions happen (otherwise plain reads of
     * [isGuestMode] / [isLoggedIn] wouldn't trigger recomposition).
     */
    data class AuthState(val isLoggedIn: Boolean, val isGuestMode: Boolean)

    private val _authStateFlow = MutableStateFlow(AuthState(isLoggedIn = false, isGuestMode = false))
    val authStateFlow: StateFlow<AuthState> = _authStateFlow.asStateFlow()

    private fun publishAuthState() {
        _authStateFlow.value = AuthState(isLoggedIn = isLoggedIn, isGuestMode = isGuestMode)
    }

    var onTokensPersisted: ((accessToken: String?, refreshToken: String?) -> Unit)? = null
    var onSessionExpired: (() -> Unit)? = null

    /**
     * Fired whenever the auth identity changes (login / register / guest-register / logout),
     * with the recomputed analytics subject key. The analytics layer subscribes to this so
     * post-login events are attributed to the right subject instead of the boot-time guest.
     * Set from the DI layer to avoid a TokenManager → AnalyticsClient circular dependency.
     */
    var onSubjectChanged: ((subjectKey: String) -> Unit)? = null

    /**
     * Current analytics subject key. Mirrors the derivation in the analytics DI bootstrap:
     * `"u:<userId>"` for a registered user, `"g:<id>"` for a guest, `"g:unknown"` otherwise.
     */
    fun currentSubjectKey(): String = when {
        accessToken != null && !isGuest -> "u:${userId ?: "authenticated"}"
        isGuest -> "g:${userId ?: "unknown"}"
        else -> "g:unknown"
    }

    private fun publishSubjectChanged() {
        onSubjectChanged?.invoke(currentSubjectKey())
    }

    fun init(settings: Settings) {
        this.settings = settings
        memoryAccessToken = settings.getStringOrNull(KEY_TOKEN)
        memoryRefreshToken = settings.getStringOrNull(KEY_REFRESH_TOKEN)
        memoryDeviceToken = settings.getStringOrNull(KEY_DEVICE_TOKEN)
        memoryIsGuest = settings.getBoolean(KEY_IS_GUEST, false)
        memoryUserId = settings.getIntOrNull(KEY_USER_ID)

        // Clean up legacy guest keys if present
        settings.remove(KEY_GUEST_TOKEN)
        settings.remove(KEY_GUEST_ID)
        settings.remove(KEY_GUEST_KASHRUT_LEVEL)
        settings.remove(KEY_GUEST_CATEGORY_IDS)

        publishAuthState()

        com.tovtalk.utils.logger.Logger.d(
            "TokenManager.init: hasAccessToken=${memoryAccessToken != null}, hasRefreshToken=${memoryRefreshToken != null}, isGuest=$memoryIsGuest",
            "TokenManager"
        )
    }

    var accessToken: String?
        get() = memoryAccessToken ?: settings?.getStringOrNull(KEY_TOKEN)
        set(value) {
            memoryAccessToken = value
            if (value != null) {
                if (settings != null) {
                    settings?.putString(KEY_TOKEN, value)
                    com.tovtalk.utils.logger.Logger.d("Access Token saved to persistence", "TokenManager")
                } else {
                    com.tovtalk.utils.logger.Logger.w("Access Token set in memory but settings=null – will not persist across refresh", "TokenManager")
                }
            } else {
                settings?.remove(KEY_TOKEN)
            }
            onTokensPersisted?.invoke(memoryAccessToken, memoryRefreshToken)
            publishAuthState()
            publishSubjectChanged()
        }

    var refreshToken: String?
        get() = memoryRefreshToken ?: settings?.getStringOrNull(KEY_REFRESH_TOKEN)
        set(value) {
            memoryRefreshToken = value
            if (value != null) {
                if (settings != null) {
                    settings?.putString(KEY_REFRESH_TOKEN, value)
                    com.tovtalk.utils.logger.Logger.d("Refresh Token saved to persistence", "TokenManager")
                } else {
                    com.tovtalk.utils.logger.Logger.w("Refresh Token set in memory but settings=null – will not persist across refresh", "TokenManager")
                }
            } else {
                settings?.remove(KEY_REFRESH_TOKEN)
            }
            onTokensPersisted?.invoke(memoryAccessToken, memoryRefreshToken)
        }

    var deviceToken: String?
        get() = memoryDeviceToken ?: settings?.getStringOrNull(KEY_DEVICE_TOKEN)
        set(value) {
            memoryDeviceToken = value
            if (value != null) {
                settings?.putString(KEY_DEVICE_TOKEN, value)
            } else {
                settings?.remove(KEY_DEVICE_TOKEN)
            }
        }

    var isGuest: Boolean
        get() = memoryIsGuest
        set(value) {
            memoryIsGuest = value
            settings?.putBoolean(KEY_IS_GUEST, value)
            publishAuthState()
            publishSubjectChanged()
        }

    var userId: Int?
        get() = memoryUserId ?: settings?.getIntOrNull(KEY_USER_ID)
        set(value) {
            memoryUserId = value
            if (value != null) {
                settings?.putInt(KEY_USER_ID, value)
            } else {
                settings?.remove(KEY_USER_ID)
            }
            publishSubjectChanged()
        }

    /**
     * Atomic (access, refresh) pair read in a single critical section.
     *
     * The Ktor Auth plugin's loadTokens previously read [accessToken] and
     * [refreshToken] via two separate getters; a concurrent refresh writing the
     * pair in between could hand the plugin a torn (new access / old refresh)
     * combination. Reading both backing fields together avoids that race.
     *
     * Both setters and [clear] mutate the same backing fields, so callers see a
     * consistent pair. Kept lightweight (plain field reads) to avoid any chance
     * of deadlocking the Ktor auth plugin that calls into here.
     */
    fun tokenSnapshot(): Pair<String?, String?> {
        val access = memoryAccessToken ?: settings?.getStringOrNull(KEY_TOKEN)
        val refresh = memoryRefreshToken ?: settings?.getStringOrNull(KEY_REFRESH_TOKEN)
        return access to refresh
    }

    /** True when logged in as guest (has tokens but is_guest flag set). */
    val isGuestMode: Boolean
        get() = isGuest && accessToken != null

    /** True when user has any valid session (guest or registered). */
    val isLoggedIn: Boolean
        get() = accessToken != null

    fun clear() {
        memoryAccessToken = null
        memoryRefreshToken = null
        memoryIsGuest = false
        memoryUserId = null
        // Clear the FCM device token too. Otherwise a stale device→user mapping
        // persists across logout/account-deletion and a different account on the
        // same device can receive the previous user's notifications.
        // TODO(server): also unregister this device token on the backend; there is
        // currently no local hook to trigger that from here.
        memoryDeviceToken = null
        settings?.remove(KEY_TOKEN)
        settings?.remove(KEY_REFRESH_TOKEN)
        settings?.remove(KEY_IS_GUEST)
        settings?.remove(KEY_USER_ID)
        settings?.remove(KEY_DEVICE_TOKEN)
        onTokensPersisted?.invoke(null, null)
        publishAuthState()
        publishSubjectChanged()
    }

    fun clearAndNotifyExpired() {
        clear()
        onSessionExpired?.invoke()
    }
}
